import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import PortfolioContext from '@totem/components/services/optimize/v2/dashboard/PortfolioContext';
import SavingsKPIs from '@totem/components/services/optimize/v2/dashboard/SavingsKPIs';
import { ProgramSummary } from '@totem/components/services/optimize/v2/dashboard/types';

const { Title } = Typography;

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  complete: 'blue',
  archived: 'default',
};

const TYPE_COLORS: Record<string, string> = {
  consumption: 'blue',
  demand: 'orange',
  combined: 'purple',
};

const formatCurrency = (val: number) =>
  `$${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const formatNumber = (val: number, decimals = 0) =>
  val.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const PortfolioPanel = () => {
  const { dashboard, loading, selectedProgram, setSelectedProgram } =
    useContext(PortfolioContext);

  const columns: ColumnsType<ProgramSummary> = [
    {
      title: 'Building',
      dataIndex: 'buildingName',
      key: 'buildingName',
      sorter: (aRow, bRow) =>
        aRow.buildingName.localeCompare(bRow.buildingName),
      render: (name: string, record: ProgramSummary) => (
        <Link
          to={`/dashboard/buildings/${record.buildingId}`}
          onClick={(evt) => evt.stopPropagation()}
        >
          {name}
        </Link>
      ),
    },
    {
      title: 'Program',
      dataIndex: 'programName',
      key: 'programName',
      sorter: (aRow, bRow) => aRow.programName.localeCompare(bRow.programName),
    },
    {
      title: 'Types',
      dataIndex: 'programTypes',
      key: 'programTypes',
      render: (types: string[]) =>
        (types ?? []).map((typ) => (
          <Tag key={typ} color={TYPE_COLORS[typ] ?? 'default'}>
            {typ.charAt(0).toUpperCase() + typ.slice(1)}
          </Tag>
        )),
    },
    {
      title: 'Status',
      dataIndex: 'programStatus',
      key: 'programStatus',
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] ?? 'default'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Complete', value: 'complete' },
        { text: 'Archived', value: 'archived' },
      ],
      onFilter: (value, record) => record.programStatus === value,
    },
    {
      title: 'Total Savings',
      dataIndex: 'totalCostSavings',
      key: 'totalCostSavings',
      align: 'right',
      render: (val: number) => formatCurrency(val),
      sorter: (aRow, bRow) => aRow.totalCostSavings - bRow.totalCostSavings,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Energy Savings',
      dataIndex: 'totalEnergyCostSavings',
      key: 'totalEnergyCostSavings',
      align: 'right',
      render: (val: number) => formatCurrency(val),
      sorter: (aRow, bRow) =>
        aRow.totalEnergyCostSavings - bRow.totalEnergyCostSavings,
    },
    {
      title: 'Demand Savings',
      dataIndex: 'totalDemandCostSavings',
      key: 'totalDemandCostSavings',
      align: 'right',
      render: (val: number) => formatCurrency(val),
      sorter: (aRow, bRow) =>
        aRow.totalDemandCostSavings - bRow.totalDemandCostSavings,
    },
    {
      title: 'kWh Saved',
      dataIndex: 'totalKwhSaved',
      key: 'totalKwhSaved',
      align: 'right',
      render: (val: number) => formatNumber(val),
      sorter: (aRow, bRow) => aRow.totalKwhSaved - bRow.totalKwhSaved,
    },
    {
      title: 'Peak kW ↓',
      dataIndex: 'totalPeakKwReduced',
      key: 'totalPeakKwReduced',
      align: 'right',
      render: (val: number) => formatNumber(val, 1),
      sorter: (aRow, bRow) => aRow.totalPeakKwReduced - bRow.totalPeakKwReduced,
    },
    {
      title: 'Months',
      key: 'months',
      align: 'center',
      render: (_: unknown, record: ProgramSummary) =>
        `${record.monthsActual}A / ${record.monthsProjected}P`,
    },
    {
      title: '',
      key: 'actions',
      align: 'center',
      width: 80,
      render: (_: unknown, record: ProgramSummary) => (
        <Link
          to={`/dashboard/optimize/buildings/${record.buildingId}/programs/${record.programId}/dashboard`}
          onClick={(evt) => evt.stopPropagation()}
        >
          View
        </Link>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Title level={5} style={{ margin: 0 }}>
            Portfolio Summary
          </Title>
        </Col>
        <Col span={24}>
          <SavingsKPIs
            totalCostSavings={dashboard.totalCostSavings}
            totalEnergyCostSavings={dashboard.totalEnergyCostSavings}
            totalDemandCostSavings={dashboard.totalDemandCostSavings}
            totalKwhSaved={dashboard.totalKwhSaved}
            totalPeakKwReduced={dashboard.totalPeakKwReduced}
          />
        </Col>
        <Col span={24}>
          <Table<ProgramSummary>
            columns={columns}
            dataSource={dashboard.programs}
            rowKey="programId"
            size="small"
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            rowClassName={(record) =>
              selectedProgram?.programId === record.programId
                ? 'ant-table-row-selected'
                : ''
            }
            onRow={(record) => ({
              onClick: () =>
                setSelectedProgram(
                  selectedProgram?.programId === record.programId
                    ? null
                    : record,
                ),
              style: { cursor: 'pointer' },
            })}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <strong>Total ({dashboard.programs.length} programs)</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>{formatCurrency(dashboard.totalCostSavings)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <strong>
                    {formatCurrency(dashboard.totalEnergyCostSavings)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <strong>
                    {formatCurrency(dashboard.totalDemandCostSavings)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <strong>{formatNumber(dashboard.totalKwhSaved)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <strong>
                    {formatNumber(dashboard.totalPeakKwReduced, 1)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} />
              </Table.Summary.Row>
            )}
          />
        </Col>
      </Row>
    </Spin>
  );
};

export default PortfolioPanel;
